import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MatSuggestion, MatSuggestionsResponse } from '../models/mat-suggestion.model';

@Injectable({ providedIn: 'root' })
export class MatSuggestionsService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  private readonly _suggestions = signal<MatSuggestion[]>([]);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _unreadCount = signal<number>(0);
  private readonly _lastFetched = signal<Date | null>(null);
  private readonly _totalCount = signal<number>(0);

  readonly suggestions = computed(() => this._suggestions());
  readonly isLoading = computed(() => this._isLoading());
  readonly unreadCount = computed(() => this._unreadCount());
  readonly lastFetched = computed(() => this._lastFetched());
  readonly totalCount = computed(() => this._totalCount());
  readonly hasSuggestions = computed(() => this._suggestions().length > 0);

  fetchSuggestions(): void {
    this._isLoading.set(true);
    this.http.get<MatSuggestionsResponse>(`${this.apiUrl}/mat/suggestions`).pipe(
      catchError(() => of({ suggestions: [], totalCount: 0 }))
    ).subscribe(response => {
      const prev = this._suggestions();
      this._suggestions.set(response.suggestions);
      this._totalCount.set(response.totalCount);
      this._lastFetched.set(new Date());
      this._isLoading.set(false);

      // Count new suggestions not in the previous set
      if (prev.length > 0) {
        const prevIds = new Set(prev.map(s => s.id));
        const newCount = response.suggestions.filter(s => !prevIds.has(s.id)).length;
        if (newCount > 0) {
          this._unreadCount.update(c => c + newCount);
        }
      } else if (response.suggestions.length > 0) {
        this._unreadCount.set(response.suggestions.length);
      }
    });
  }

  startPolling(intervalMs: number = 5 * 60 * 1000): void {
    this.stopPolling();
    this.fetchSuggestions();
    this.pollingInterval = setInterval(() => this.fetchSuggestions(), intervalMs);
  }

  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  markAsRead(): void {
    this._unreadCount.set(0);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
